import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Clock, Sparkles, TrendingUp, Loader2, Send } from "lucide-react";

const BestTimeToDrop = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudging, setNudging] = useState(false);

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/growth/best-time-to-drop`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (e) {
        // Most likely "Artist profile required" — silently hide for supporters
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const sendNudge = async () => {
    setNudging(true);
    try {
      const res = await axios.post(`${API}/growth/smart-nudge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Nudge sent: ${res.data.title}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate nudge");
    } finally {
      setNudging(false);
    }
  };

  if (loading) return <div className="card p-6"><div className="h-32 animate-pulse bg-[#1A2030] rounded" /></div>;
  if (!data) return null;

  return (
    <div className="card p-6 relative overflow-hidden" data-testid="best-time-card">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#8DB87A] via-[#EC008C] to-[#FF6BC5]" />
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-10 blur-3xl bg-[#8DB87A]" />

      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#8DB87A]/15 border border-[#8DB87A]/30 flex items-center justify-center">
              <Clock className="w-6 h-6 text-[#8DB87A]" />
            </div>
            <div>
              <h3 className="font-['Unbounded'] text-xl font-bold text-white">Best Time To Drop</h3>
              <p className="text-xs text-[#8A92A0]">AI growth coach, powered by your hourly fan traffic.</p>
            </div>
          </div>
        </div>

        {!data.has_enough_data ? (
          <div className="bg-[#050810] rounded-xl p-5 border border-[#1A2030] text-center">
            <TrendingUp className="w-8 h-8 text-[#8A92A0] mx-auto mb-2" />
            <p className="text-[#8A92A0] text-sm">{data.message}</p>
          </div>
        ) : (
          <>
            {/* Peak hour highlight */}
            <div className="bg-gradient-to-r from-[#8DB87A]/10 to-[#EC008C]/10 rounded-xl p-5 border border-[#8DB87A]/30 mb-4">
              <p className="text-xs text-[#8A92A0] uppercase tracking-wide mb-1">Your Peak Hour</p>
              <div className="flex items-baseline gap-3">
                <p className="text-4xl font-['Unbounded'] font-bold text-[#EC008C]" data-testid="peak-hour-display">{data.peak_hour.label}</p>
                <p className="text-sm text-[#8A92A0]">{data.peak_hour.share_pct}% of fan traffic ({data.bucket})</p>
              </div>
            </div>

            {/* Top 3 */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              {data.top_hours.map((h, i) => (
                <div key={h.hour} className="bg-[#050810] rounded-lg p-3 border border-[#1A2030] text-center">
                  <p className="text-[10px] text-[#4A5260] uppercase">#{i + 1}</p>
                  <p className="text-lg font-bold text-white">{h.label}</p>
                  <p className="text-[10px] text-[#8A92A0]">{h.share_pct}%</p>
                </div>
              ))}
            </div>

            {/* AI tip */}
            {data.ai_tip && (
              <div className="bg-[#050810] rounded-xl p-4 border border-[#EC008C]/20 mb-4" data-testid="ai-tip">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-[#EC008C] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-white leading-relaxed">{data.ai_tip}</p>
                </div>
              </div>
            )}
          </>
        )}

        {/* Send smart nudge button */}
        <button
          onClick={sendNudge}
          disabled={nudging}
          className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#EC008C]/20 to-[#FF6BC5]/20 text-[#EC008C] hover:from-[#EC008C]/30 hover:to-[#FF6BC5]/30 transition-colors flex items-center justify-center gap-2 text-sm font-medium border border-[#EC008C]/30 disabled:opacity-60"
          data-testid="smart-nudge-btn"
        >
          {nudging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {nudging ? "Crafting your nudge..." : "Send me an AI Smart Nudge"}
        </button>
      </div>
    </div>
  );
};

export default BestTimeToDrop;
